import { useState, useCallback } from "react";

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: "default" | "destructive" | "success";
  duration?: number;
}

interface ToastState {
  toasts: Toast[];
}

interface ToastActions {
  toast: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// Simple in-memory toast state (in a real app, this might use a context or global state)
let toastState: ToastState = { toasts: [] };
let listeners: Array<(state: ToastState) => void> = [];

const notifyListeners = () => {
  listeners.forEach((listener) => listener(toastState));
};

const generateId = () => Math.random().toString(36).substr(2, 9);

export const useToast = (): ToastActions & ToastState => {
  const [state, setState] = useState<ToastState>(toastState);

  // Subscribe to state changes
  const updateState = useCallback((newState: ToastState) => {
    setState(newState);
  }, []);

  // Add listener on mount
  if (!listeners.includes(updateState)) {
    listeners.push(updateState);
  }

  const toast = useCallback((toastData: Omit<Toast, "id">) => {
    const newToast: Toast = {
      ...toastData,
      id: generateId(),
      duration: toastData.duration ?? 5000,
    };

    toastState = {
      ...toastState,
      toasts: [...toastState.toasts, newToast],
    };

    notifyListeners();

    // Auto-dismiss after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        dismiss(newToast.id);
      }, newToast.duration);
    }
  }, []);

  const dismiss = useCallback((id: string) => {
    toastState = {
      ...toastState,
      toasts: toastState.toasts.filter((toast) => toast.id !== id),
    };
    notifyListeners();
  }, []);

  const dismissAll = useCallback(() => {
    toastState = { toasts: [] };
    notifyListeners();
  }, []);

  return {
    ...state,
    toast,
    dismiss,
    dismissAll,
  };
};
